import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { adminApi } from '../../api/admin.api';

type PeriodType = 'TODAY' | 'HOURLY' | 'DAILY' | 'WEEKLY' | 'MONTHLY';

/**
 * 사용자 증가 추이 라인 차트
 */
export const UserGrowthChart: React.FC = () => {
  const [period, setPeriod] = useState<PeriodType>('TODAY');

  const { data: graphData, isLoading } = useQuery({
    queryKey: ['graphData'],
    queryFn: async () => {
      const response = await adminApi.getGraphData();
      return response.data;
    },
  });

  const growthData = useMemo(() => {
    if (!graphData) return undefined;
    const key = period.toLowerCase() as 'today' | 'hourly' | 'daily' | 'weekly' | 'monthly';
    return graphData.userGrowth[key];
  }, [graphData, period]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 border border-[#E5E7EB]">
        <h3 className="text-lg font-semibold text-[#030213] mb-4">사용자 증가 추이</h3>
        <div className="h-[300px] flex items-center justify-center">
          <p className="text-[#6B7280]">로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#030213]">사용자 증가 추이</h3>
        <div className="flex gap-2">
          <button
            onClick={() => setPeriod('TODAY')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              period === 'TODAY'
                ? 'bg-[#3B82F6] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            오늘
          </button>
          <button
            onClick={() => setPeriod('HOURLY')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              period === 'HOURLY'
                ? 'bg-[#3B82F6] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            시간별
          </button>
          <button
            onClick={() => setPeriod('DAILY')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              period === 'DAILY'
                ? 'bg-[#3B82F6] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            일별
          </button>
          <button
            onClick={() => setPeriod('WEEKLY')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              period === 'WEEKLY'
                ? 'bg-[#3B82F6] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            주별
          </button>
          <button
            onClick={() => setPeriod('MONTHLY')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              period === 'MONTHLY'
                ? 'bg-[#3B82F6] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            월별
          </button>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm p-6 border border-[#E5E7EB]">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={growthData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis
              dataKey="month"
              stroke="#6B7280"
              style={{ fontSize: '12px' }}
            />
            <YAxis
              stroke="#6B7280"
              style={{ fontSize: '12px' }}
              width={45}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '12px',
              }}
            />
            <Line
              type="monotone"
              dataKey="userCount"
              stroke="#3B82F6"
              strokeWidth={2}
              dot={{ fill: '#3B82F6', r: 4 }}
              activeDot={{ r: 6 }}
              name="사용자 수"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
